import React, { useState, useEffect } from 'react';
import RangeSlider from './RangeSlider';
import MultiCheckbox from './MultiCheckbox';

// Isolate Modal component
const Modal = React.memo(({ isOpen, toggleModal, priceRangeDefaults, discountRangeDefaults, onPriceChange, onDiscountChange, categoryOptions, selectedCategories, onCategoryChange }) => {


  return (
    <div className="bg-white shadow-lg rounded-lg w-full my-4 p-6 lg:max-w-[320px]">
      <h3 className="text-xl font-headings font-bold mb-2">Filter Your Results Here</h3>
      <hr className="border-cr-gray mb-4" />

      <div id="filter-categories" className="mb-4">
        <h4 className="text-lg font-bold mb-2">Categories</h4>
        <MultiCheckbox
          options={categoryOptions}
          selected={selectedCategories}
          onChange={onCategoryChange}
        />
      </div>

      <RangeSliderSection
        label="Price Range"
        min={Number(priceRangeDefaults.min)}
        max={Number(priceRangeDefaults.max)}
        start={Number(priceRangeDefaults.start)}
        end={Number(priceRangeDefaults.end)}
        append="$"
        onValueChange={onPriceChange}
      />

      <RangeSliderSection
        label="Discount Range"
        min={Number(discountRangeDefaults.min)}
        max={Number(discountRangeDefaults.max)}
        start={Number(discountRangeDefaults.start)}
        end={Number(discountRangeDefaults.end)}
        prepand="%"
        onValueChange={onDiscountChange}
      />
    </div>
  );
});

// Component for rendering RangeSlider with a label
const RangeSliderSection = ({ label, min, max, start, end, append, prepand, onValueChange }) => (
  <div id={`filter-${label.toLowerCase().replace(" ", "-")}`} className="mb-6">
    <h4 className="text-lg font-bold mb-1">{label}</h4>
    <RangeSlider
      min={min}
      max={max}
      start={start}
      end={end}
      append={append}
      prepand={prepand}
      onValueChange={onValueChange}
    />
  </div>
);

const formatPrice = (price) => {
  const formattedPrice = Number(price).toFixed(2);
  return formattedPrice.endsWith('.00') ? formattedPrice.slice(0, -3) : formattedPrice;
};



function BestDealsComponent({ data }) {
  const { price_range: priceRangeDefaults, discount_range: discountRangeDefaults } = data.filter;
  const categoryOptions = data.filter.categories.map(category => ({
    id: category.term_id,
    name: category.name,
  }));

  const [priceRange, setPriceRange] = useState({ min: Number(priceRangeDefaults.start), max: Number(priceRangeDefaults.end) });
  const [discountRange, setDiscountRange] = useState({ min: Number(discountRangeDefaults.start), max: Number(discountRangeDefaults.end) });
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filteredDeals, setFilteredDeals] = useState(data.deals);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCategoryChange = (category) => {
    setSelectedCategories(prevSelected => {
      if (prevSelected.includes(category)) {
        return prevSelected.filter(c => c !== category);
      } else {
        return [...prevSelected, category];
      }
    });
  };

  useEffect(() => {
    const filtered = data.deals.filter(product => (
      product.price >= priceRange.min && product.price <= priceRange.max &&
      product.discount >= discountRange.min && product.discount <= discountRange.max &&
      (selectedCategories.length === 0 || selectedCategories.includes(product.category))
    ));
    setFilteredDeals(filtered);
  }, [priceRange, discountRange, selectedCategories, data.deals]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const createMarkup = htmlString => ({ __html: htmlString });

  return (
    <div>
      <h2 id="best-deals2" className="text-2xl font-bold text-center mb-4 md:text-4xl md:mb-6">
        {data.deals_title}
      </h2>
      <p dangerouslySetInnerHTML={createMarkup(data.deals_text)} className="text-center mx-auto mb-4 md:text-xl"></p>

      <button className="btn text-white bg-link inline-flex items-center lg:hidden" onClick={toggleModal}>
        Filter <img className="ml-2" src="/wp-content/themes/cr2021/images/icons/tune.svg" alt="Filter" />
      </button>

      <div className="lg:grid lg:grid-cols-3 lg:gap-4">
        <div className={`lg:col-span-1 ${isModalOpen ? '' : 'hidden lg:block'}`}>
          <Modal
            isOpen={true}
            toggleModal={toggleModal}
            priceRangeDefaults={priceRangeDefaults}
            discountRangeDefaults={discountRangeDefaults}
            onPriceChange={setPriceRange}
            onDiscountChange={setDiscountRange}
            categoryOptions={categoryOptions}
            selectedCategories={selectedCategories}
            onCategoryChange={handleCategoryChange}
          />
        </div>


        <div className="flex snap-x overflow-auto flex-nowrap py-4 gap-4 lg:col-span-2 lg:grid lg:grid-cols-2">
          {filteredDeals.map(product => (
            <div key={product.post_id} className="p-4 min-w-[236px] max-w-[320px] md:min-w-[320px] rounded-lg snap-center bg-white shadow-lg lg:min-w-full">
              <div className="flex items-center h-full">
                <div className="flex flex-col w-full h-full justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-center md:text-2xl">{product.title}</h3>
                    <div className="relative">
                      <img src={product.image} alt={product.title} className="h-40 m-auto mb-4" />
                      <div
                        className="absolute top-0 right-0 text-sm p-2 leading-4 rounded-full font-bold w-16 h-16 text-white flex items-center text-center"
                        style={{
                          backgroundImage: "url('/wp-content/themes/cr2021/images/icons/star.svg')",
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'center',
                        }}>
                        {product.discount}% OFF
                      </div>
                    </div>
                    <div className="text-center text-lg font-bold mb-2 md:text-2xl">Today's Deal</div>
                    <div className="text-2xl font-bold text-center mb- text-cr-green md:text-4xl">
                      {product.currency + '' + product.price}
                    </div>
                    <div className="text-center mb-4 md:text-xl">
                      <span className="line-through text-gray-700">{product.currency + '' + formatPrice(product.priceOld)}</span>
                    </div>
                  </div>
                  <div className="mb-4 bg-cr-green/20 -mx-4 text-center p-2 md:text-xl font-bold md:font-bold">
                    <span>Save</span>&nbsp;
                    <span className="text-cr-green">{product.currency + '' + formatPrice(product.discount_value)}</span>
                  </div>
                  <div className="mb-2 flex justify-center" dangerouslySetInnerHTML={createMarkup(product.buy_props.button)} />
                  <div className="text-center">
                    <a href={product.link_to_specs} className="link text-sm mb-2 md:text-lg" target="_blank">
                      See Full Specs &gt;
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>


    </div>
  );
}

export default BestDealsComponent;
